import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import RegisterServicePicker from '../../face-identity/components/RegisterServicePicker';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import {
  recordSelectedServices,
  useRegisterServicesCatalog,
  useServiceSelection,
} from '../../face-identity/hooks/useRegisterServices';
import {
  countSelectedServiceUnits,
  formatDisplayPrice,
  sumSelectedServicesTotal,
  summarizeSelectedServices,
} from '../../face-identity/utils/servicePriceOptions';
import { buildKioskNavState } from '../../face-identity/utils/kioskNavState';
import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import {
  assignStaffToCheckin,
  createPartnerCheckinQueue,
  fetchActiveVisit,
  fetchOpenVisits,
  startServiceCheckinQueue,
} from '../../salon-admin/utils/checkinQueueApi';
import { fetchFranchiseStaffCatalog } from '../../admin-shared/franchiseStaffCatalogApi';
import {
  isStaffOnDeskLeave,
  staffDeskPickSubtitle,
  staffDeskUnavailable,
} from '../utils/staffDeskPick';
import { staffRoleLabel } from '../../admin-shared/staffRoleOptions';
import {
  SALON_SERVICE_LOBBY_PATH,
  SALON_SERVICE_WORK_PATH,
} from '../config/salonApp';
import {
  getActiveCheckinSession,
  getSalonEntrySource,
  getSalonFaceUserId,
  setActiveCheckinSession,
} from '../utils/salonFaceSession';
import { pendingAnalysisLines, readLocalPendingServiceLines } from '../utils/analysisServiceSync';
import { alignPendingLinesWithCatalog, collectOccupiedStylistIds } from '../utils/serviceLineUtils';
import {
  filterStaffForSelectedServices,
  staffFilterHint as buildStaffFilterHint,
} from '../utils/staffServiceMatch';
import { pinServiceWork } from '../utils/serviceWorkStorage';
import { persistSelectedServicesForRecommendations } from '../utils/visitHandoff';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import { serviceSessionHeroImage } from '../utils/serviceSessionVisuals';
import {
  readFaceProfileSnapshot,
} from '../../face-identity/utils/faceIdentityApi';
import {
  normalizeSalonGuestGender,
  resolveSalonGuestGender,
} from '../utils/bootstrapFaceSalonSession';
import { getFaceUserProfile } from '../../face-identity/utils/faceIdentityApi';
import { setSalonGuestPii } from '../utils/salonGuestPiiStore';
import {
  applyCheckinToGuestSession,
  customerFieldsFromCheckin,
} from '../utils/visitHandoff';
import { getFranchiseId } from '../../shared/franchiseFlowAuth';
import AddServiceModal from '../../engagement/components/AddServiceModal';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import ServiceSessionHubChooseCard from '../components/ServiceSessionHubChooseCard';
import ServiceSessionCartModal from '../components/ServiceSessionCartModal';
import './ServiceSessionPage.css';

async function fetchStaffCatalog() {
  return fetchFranchiseStaffCatalog();
}

function greetingFirstName(fullName) {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0] || trimmed;
}

function normalizeServiceList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'string') {
        const name = item.trim();
        return name ? { id: name, name } : null;
      }
      const name = String(item.name || item.service_name || item.label || '').trim();
      if (!name) return null;
      const entry = {
        id: String(item.id || item.service_id || name),
        name,
      };
      const price = String(item.price || item.price_text || '').trim();
      const category = String(item.category || '').trim();
      const description = String(item.description || '').trim();
      const baseServiceId = String(item.base_service_id || '').trim();
      const optionKey = String(item.option_key || '').trim();
      const optionLabel = String(item.option_label || '').trim();
      if (price) entry.price = price;
      if (category) entry.category = category;
      if (description) entry.description = description;
      if (baseServiceId) entry.base_service_id = baseServiceId;
      if (optionKey) entry.option_key = optionKey;
      if (optionLabel) entry.option_label = optionLabel;
      if (item.amount != null && item.amount !== '') {
        const amount = Number(item.amount);
        if (Number.isFinite(amount)) entry.amount = amount;
      }
      if (item.net_price != null && item.net_price !== '') entry.net_price = item.net_price;
      if (item.sale_price != null && item.sale_price !== '') entry.sale_price = item.sale_price;
      if (item.discount != null && item.discount !== '') entry.discount = item.discount;
      if (item.quantity != null && Number(item.quantity) > 0) {
        entry.quantity = Math.max(1, Number(item.quantity) || 1);
      }
      return entry;
    })
    .filter(Boolean);
}

function expandSelectedForStart(selected) {
  return (selected || []).flatMap((svc) => {
    const base = normalizeServiceList([svc])[0];
    if (!base) return [];
    const qty = Math.max(1, Number(svc.quantity) || 1);
    return Array.from({ length: qty }, () => ({ ...base, quantity: 1 }));
  });
}

function staffInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function isFinishedVisitError(err) {
  const msg = String(err?.message || err || '').toLowerCase();
  return msg.includes('finished visit') || msg.includes('cannot update services') || msg.includes('cannot assign staff') || msg.includes('cannot start service');
}

export default function ServiceSessionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  useFaceIdentityScrollShell();
  const homePath = resolveSalonHomePath();
  const navState = buildKioskNavState(location.state);
  const routeCheckinId = location.state?.checkinId || navState.checkinId;
  const routeStylistId = String(location.state?.stylistId || '').trim();
  const routeStylistName = String(location.state?.stylistName || '').trim();
  const addMoreMode = Boolean(location.state?.addMore);
  const snapshot = readFaceProfileSnapshot() || location.state?.faceProfile || {};
  const user = snapshot.user || {};
  const faceUserId = String(user?.id || snapshot.faceUserId || getSalonFaceUserId() || '').trim();
  // Stabilize array identity — normalizeServiceList() returns a new [] every render and
  // was re-triggering the load effect (endless Loading… + active-visit storm).
  const prefillPendingKey = JSON.stringify(location.state?.prefillPendingServices || []);
  const routePrefillPending = useMemo(
    () => normalizeServiceList(location.state?.prefillPendingServices || []),
    [prefillPendingKey], // eslint-disable-line react-hooks/exhaustive-deps -- deep compare via key
  );
  const [checkinId, setCheckinId] = useState(routeCheckinId || null);
  const [visitGender, setVisitGender] = useState('');
  const [visitFaceUserId, setVisitFaceUserId] = useState('');
  const [profileGender, setProfileGender] = useState('');
  const guestGender = useMemo(
    () =>
      resolveSalonGuestGender(
        {
          ...location.state,
          checkinId: checkinId || routeCheckinId,
          faceProfile: snapshot,
          userData: { ...(location.state?.userData || {}), ...user },
          gender: location.state?.gender || snapshot.gender || snapshot.profile?.gender,
        },
        { visitGender, checkinId: checkinId || routeCheckinId },
      ),
    [location.state, snapshot, user, visitGender, checkinId, routeCheckinId],
  );
  const profileLookupId = useMemo(() => {
    const candidates = [visitFaceUserId, faceUserId, getSalonFaceUserId()];
    for (const c of candidates) {
      const id = String(c || '').trim();
      if (id && !id.startsWith('checkin:')) return id;
    }
    return '';
  }, [visitFaceUserId, faceUserId]);
  /** DB profile wins over stale kiosk handoff / last-customer PII. */
  const catalogGender = profileGender || guestGender;

  const [staffCatalog, setStaffCatalog] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  // engagement_booking_id from the visit snapshot (booking_snapshot_patch). Not the appointment id.
  const [engagementBookingId, setEngagementBookingId] = useState(null);
  const [engagementFranchiseId, setEngagementFranchiseId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [occupiedStylistIds, setOccupiedStylistIds] = useState(() => new Set());

  const lastTakenServices = useMemo(
    () =>
      normalizeServiceList(
        snapshot.lastSelectedServices ||
          snapshot.last_selected_services ||
          location.state?.lastSelectedServices ||
          [],
      ),
    [location.state?.lastSelectedServices, snapshot.lastSelectedServices, snapshot.last_selected_services],
  );

  const {
    services: serviceOptions,
    loading: servicesLoading,
    error: servicesCatalogError,
  } = useRegisterServicesCatalog(catalogGender, { requireGender: false });

  useEffect(() => {
    const fid = profileLookupId;
    if (!fid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getFaceUserProfile(fid);
        const u = data?.user || data?.profile || {};
        const prefs = data?.profile?.preferences || u?.preferences || {};
        const raw =
          u.gender ||
          prefs.gender ||
          prefs.Gender ||
          u?.profile?.gender ||
          data?.gender;
        const normalized = normalizeSalonGuestGender(raw);
        if (!cancelled && normalized) {
          setProfileGender(normalized);
          setSalonGuestPii({ gender: raw });
        }
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileLookupId]);
  const {
    selected: selectedServices,
    toggle: toggleService,
    changeQuantity: changeServiceQuantity,
    setSelected,
  } = useServiceSelection([]);
  const [categoryRailHost, setCategoryRailHost] = useState(null);
  // The visit's own pending lines decide what is preselected; lines kept in the browser (an earlier guest in
  // this tab) are used only when no visit is open yet (see the load effect).
  const [pendingPrefill, setPendingPrefill] = useState(() => routePrefillPending);

  const applyPendingPrefill = useCallback(
    (rawLines, catalog) => {
      const normalized = normalizeServiceList(rawLines);
      if (!normalized.length) return;
      const aligned = catalog?.length
        ? alignPendingLinesWithCatalog(normalized, catalog)
        : normalized;
      setSelected((prev) => (prev.length ? prev : aligned));
      const fromBooking = normalized.some((line) => line?.analysis_source === 'engagement_booking');
      setNotice(
        fromBooking
          ? `${aligned.length} booked service(s) ready — check the stylist, add services if needed, then Start.`
          : `${aligned.length} service(s) ready from analysis — pick stylist if needed, then Start.`,
      );
    },
    [setSelected],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const list = await fetchStaffCatalog();
        let visit = null;
        if (routeCheckinId) {
          visit = await fetchActiveVisit({ checkinId: routeCheckinId });
        } else if (faceUserId) {
          visit = await fetchActiveVisit({ faceUserId });
        } else {
          const session = getActiveCheckinSession();
          if (session?.id) {
            visit = await fetchActiveVisit({ checkinId: session.id });
          }
        }

        if (cancelled) return;

        const open = await fetchOpenVisits();
        const excludeId = visit?.id || routeCheckinId || null;
        const occupied = collectOccupiedStylistIds(open, excludeId);
        if (!cancelled) {
          setOccupiedStylistIds(occupied);
          setStaffCatalog(list);
          if (list.length === 1 && !routeStylistId && !occupied.has(String(list[0].id))) {
            setSelectedStaff([{ id: list[0].id, name: list[0].name }]);
          }
        }

        if (visit?.id) {
          applyCheckinToGuestSession(visit);
          const fields = customerFieldsFromCheckin(visit);
          const visitGenderNorm = normalizeSalonGuestGender(fields.gender);
          if (visitGenderNorm) setVisitGender(visitGenderNorm);
          const visitFace = String(
            visit.face_user_id || visit.customer_snapshot?.face_user_id || faceUserId || '',
          ).trim();
          if (visitFace && !visitFace.startsWith('checkin:')) {
            setVisitFaceUserId(visitFace);
          }
          setCheckinId(visit.id);
          setActiveCheckinSession({
            id: visit.id,
            code: visit.customer_code,
            status: visit.status,
            faceUserId: visitFace || faceUserId,
          });
          if (routeStylistId && routeStylistName) {
            setSelectedStaff([{ id: routeStylistId, name: routeStylistName }]);
          } else if (Array.isArray(visit.assigned_staff) && visit.assigned_staff.length) {
            // The booked stylist (check-in from an engagement booking), still changeable.
            setSelectedStaff(visit.assigned_staff.map((p) => ({ id: String(p.id), name: p.name })));
          }
          const pending = pendingAnalysisLines(visit.selected_services);
          if (pending.length) {
            setPendingPrefill(normalizeServiceList(pending));
          } else if (routePrefillPending.length) {
            setPendingPrefill(routePrefillPending);
          }
          const visitSnap = visit.customer_snapshot && typeof visit.customer_snapshot === 'object'
            ? visit.customer_snapshot
            : {};
          const bookingNum = Number(visitSnap.engagement_booking_id);
          setEngagementBookingId(Number.isFinite(bookingNum) && bookingNum > 0 ? bookingNum : null);
          const franchiseRaw = visit.franchise_id ?? visitSnap.franchise_id;
          setEngagementFranchiseId(
            franchiseRaw != null && String(franchiseRaw).trim() !== '' ? franchiseRaw : null,
          );
        } else {
          if (!routeCheckinId) setCheckinId(null);
          setEngagementBookingId(null);
          setEngagementFranchiseId(null);
          if (!routePrefillPending.length) {
            const stored = normalizeServiceList(readLocalPendingServiceLines());
            if (stored.length) setPendingPrefill(stored);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load staff');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [faceUserId, routeCheckinId, routeStylistId, routeStylistName, routePrefillPending]);

  useEffect(() => {
    if (!pendingPrefill.length) return;
    applyPendingPrefill(pendingPrefill, serviceOptions);
  }, [pendingPrefill, serviceOptions, applyPendingPrefill]);

  const refreshStylistDeskState = useCallback(async () => {
    try {
      const [open, staff] = await Promise.all([fetchOpenVisits(), fetchStaffCatalog()]);
      setOccupiedStylistIds(collectOccupiedStylistIds(open, checkinId));
      setStaffCatalog(staff);
    } catch {
      try {
        const open = await fetchOpenVisits();
        setOccupiedStylistIds(collectOccupiedStylistIds(open, checkinId));
      } catch {
        /* non-blocking */
      }
    }
  }, [checkinId]);

  useEffect(() => {
    const onQueue = () => { refreshStylistDeskState(); };
    window.addEventListener('checkin-queue-updated', onQueue);
    const timer = window.setInterval(refreshStylistDeskState, 12000);
    return () => {
      window.removeEventListener('checkin-queue-updated', onQueue);
      window.clearInterval(timer);
    };
  }, [refreshStylistDeskState]);

  const onLeaveStylistIds = useMemo(
    () => new Set(staffCatalog.filter(isStaffOnDeskLeave).map((p) => String(p.id))),
    [staffCatalog],
  );

  useEffect(() => {
    if (routeStylistId || addMoreMode) return;
    setSelectedStaff((prev) =>
      prev.filter(
        (p) =>
          !occupiedStylistIds.has(String(p.id)) && !onLeaveStylistIds.has(String(p.id)),
      ),
    );
  }, [occupiedStylistIds, onLeaveStylistIds, routeStylistId, addMoreMode]);

  const visibleStaff = useMemo(
    () => filterStaffForSelectedServices(staffCatalog, selectedServices, serviceOptions),
    [staffCatalog, selectedServices, serviceOptions],
  );

  const staffTierHint = useMemo(
    () => buildStaffFilterHint(selectedServices, serviceOptions),
    [selectedServices, serviceOptions],
  );

  useEffect(() => {
    if (routeStylistId) return;
    setSelectedStaff((prev) =>
      prev.filter((p) => visibleStaff.some((v) => String(v.id) === String(p.id))),
    );
  }, [visibleStaff, routeStylistId]);

  useEffect(() => {
    if (routeStylistId || !visibleStaff.length) return;
    if (visibleStaff.length !== 1) return;
    const only = visibleStaff[0];
    if (staffDeskUnavailable(only, occupiedStylistIds)) return;
    setSelectedStaff((prev) => {
      if (prev.length) return prev;
      return [{ id: only.id, name: only.name }];
    });
  }, [visibleStaff, occupiedStylistIds, routeStylistId]);

  const staffSelectedKeys = useMemo(
    () => new Set(selectedStaff.map((s) => String(s.id))),
    [selectedStaff],
  );

  const selectStaff = (person) => {
    if (staffDeskUnavailable(person, occupiedStylistIds)) return;
    setSelectedStaff([{ id: person.id, name: person.name }]);
  };

  const applyLastTaken = () => {
    if (!lastTakenServices.length) return;
    setSelected(lastTakenServices);
    setNotice('Last taken services applied — adjust if needed, then Start service.');
  };

  const bindCheckin = useCallback(
    (createdOrVisit) => {
      const id = createdOrVisit?.id;
      if (!id) return null;
      const visitFace = String(
        createdOrVisit.face_user_id ||
          createdOrVisit.customer_snapshot?.face_user_id ||
          faceUserId ||
          '',
      ).trim();
      setActiveCheckinSession({
        id,
        code: createdOrVisit.customer_code,
        status: createdOrVisit.status || 'active',
        faceUserId: visitFace,
      });
      setCheckinId(id);
      return id;
    },
    [faceUserId],
  );

  const ensureCheckin = useCallback(async ({ forceNew = false } = {}) => {
    if ((checkinId || routeCheckinId) && !forceNew) {
      const existingId = checkinId || routeCheckinId;
      // Confirm stored id still belongs to this guest.
      const visit = await fetchActiveVisit({
        checkinId: existingId,
        faceUserId: faceUserId || undefined,
      });
      if (visit?.id) return visit.id;
      if (!faceUserId) return existingId;
    }
    if (!forceNew && faceUserId) {
      const open = await fetchActiveVisit({ faceUserId });
      if (open?.id) {
        bindCheckin(open);
        return open.id;
      }
    }
    const created = await createPartnerCheckinQueue({
      faceUserId: faceUserId || user?.id,
      customerName: user?.name || user?.full_name || 'Guest',
      mobile: user?.mobile || user?.phone || '',
      selectedServices: [],
      forceNew: Boolean(forceNew),
      customerSnapshot: {
        name: user?.name || user?.full_name || 'Guest',
        full_name: user?.name || user?.full_name || 'Guest',
        mobile: user?.mobile || user?.phone || '',
        face_user_id: faceUserId || user?.id,
        organization_id: user?.organization_id || snapshot.organizationId || undefined,
        last_selected_services: lastTakenServices,
        selected_services: [],
        is_new_member: snapshot.isNewMember,
      },
      source: getSalonEntrySource() || 'salon_face',
      startService: false,
    });
    const id = bindCheckin(created);
    if (!id) throw new Error('Failed to create visit check-in');
    return id;
  }, [
    bindCheckin,
    checkinId,
    routeCheckinId,
    faceUserId,
    lastTakenServices,
    snapshot.isNewMember,
    snapshot.organizationId,
    user,
  ]);

  const startService = async () => {
    if (busy) return;
    if (!selectedStaff.length) {
      setError('Select who is performing the service');
      return;
    }
    if (!selectedServices.length) {
      setError('Select at least one service to start');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const expanded = expandSelectedForStart(selectedServices);
      const withStaff = expanded.map((svc) => ({
        ...svc,
        staff: selectedStaff,
      }));
      let id = checkinId || (await ensureCheckin());
      try {
        // Assign + lock only at start-service time.
        await assignStaffToCheckin(id, selectedStaff);
        await startServiceCheckinQueue(id, {
          assignedStaff: selectedStaff,
          selectedServices: withStaff,
        });
      } catch (err) {
        if (!isFinishedVisitError(err)) throw err;
        id = await ensureCheckin({ forceNew: true });
        await assignStaffToCheckin(id, selectedStaff);
        setNotice('Started a new visit for this guest.');
        await startServiceCheckinQueue(id, {
          assignedStaff: selectedStaff,
          selectedServices: withStaff,
        });
      }
      persistSelectedServicesForRecommendations(withStaff);
      setActiveCheckinSession({
        id,
        code: location.state?.checkinCode || getActiveCheckinSession()?.code,
        status: 'in_service',
        faceUserId,
      });
      const stylist = selectedStaff[0] || {};
      pinServiceWork({
        checkinId: id,
        customerCode: location.state?.checkinCode,
        customerName: location.state?.customerName || user?.name,
        stylistId: stylist.id,
        stylistName: stylist.name,
      });
      try {
        window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
      } catch (_) {
        /* ignore */
      }
      try {
        await recordSelectedServices({
          faceUserId: user?.id || faceUserId,
          selectedServices: withStaff,
          source: 'salon_service_session',
          visitId: id,
        });
      } catch (_) {
        /* non-blocking */
      }
      navigate(SALON_SERVICE_WORK_PATH, {
        replace: true,
        state: {
          checkinId: id,
          checkinCode: location.state?.checkinCode,
          customerName: location.state?.customerName || user?.name,
          stylistId: stylist.id,
          stylistName: stylist.name,
        },
      });
    } catch (err) {
      setError(err.message || 'Failed to start service');
    } finally {
      setBusy(false);
    }
  };

  const guestName = useMemo(() => {
    const name =
      location.state?.customerName ||
      user?.name ||
      user?.full_name ||
      '';
    return String(name || '').trim();
  }, [location.state?.customerName, user]);

  const selectedServiceUnits = useMemo(
    () => countSelectedServiceUnits(selectedServices),
    [selectedServices],
  );

  const selectedServicesTotal = useMemo(
    () => sumSelectedServicesTotal(selectedServices, serviceOptions),
    [selectedServices, serviceOptions],
  );

  const selectedServicesSummary = useMemo(
    () => summarizeSelectedServices(selectedServices, serviceOptions),
    [selectedServices, serviceOptions],
  );

  const heroImageUrl = useMemo(
    () => serviceSessionHeroImage(catalogGender),
    [catalogGender],
  );

  const lastServiceTakenName = useMemo(() => {
    if (!lastTakenServices.length) return '';
    const last = lastTakenServices[lastTakenServices.length - 1];
    return String(last?.name || '').trim();
  }, [lastTakenServices]);

  const heroGreeting = useMemo(() => {
    const first = greetingFirstName(guestName);
    if (!first) {
      if (addMoreMode) {
        return { title: 'Add new service', lede: 'Choose what to add today' };
      }
      return { title: "Let's get started", lede: 'Pick stylist, then services' };
    }
    if (addMoreMode) {
      return {
        title: `Welcome back, ${first}`,
        lede: 'Add more services for your visit',
      };
    }
    return {
      title: `Welcome, ${first}`,
      lede: 'Pick your stylist, then services',
    };
  }, [guestName, addMoreMode]);

  // Outlet for Add service: the visit's franchise, else the salon session's outlet.
  const addServiceFranchiseId = (() => {
    const raw = engagementFranchiseId != null && String(engagementFranchiseId).trim() !== ''
      ? engagementFranchiseId
      : getFranchiseId();
    if (raw == null || String(raw).trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();

  return (
    <div className="fi-lux-theme fi-scroll-page page-bg salon-service-session salon-service-session--flow salon-service-session--start">
      <div className="salon-service-session__content salon-service-start">
        <header className="salon-service-session__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
          />
          <FaceIdentityFlowNav
            homePath={homePath}
            onBack={() => navigate(SALON_SERVICE_LOBBY_PATH, { replace: false, state: navState })}
          />
        </header>

        <div className="salon-guest-pick__sheet salon-service-start__sheet">
        <div className="salon-service-start__workspace salon-service-start__workspace--aligned">
        <div className="salon-service-start__hero-row">
        <div className="salon-service-start__hub-slot">
          <ServiceSessionHubChooseCard checkinId={checkinId} />
        </div>
        <section className="salon-service-start__hero salon-service-start__hero--banner">
          <div className="salon-service-start__hero-copy">
            <p className="salon-guest-pick__eyebrow">Service session</p>
            <h1 className="salon-service-session__title salon-guest-pick__name salon-service-start__hero-greeting">
              {heroGreeting.title}
            </h1>
            <p className="salon-service-session__lede salon-guest-pick__meta salon-service-start__hero-lede">
              {heroGreeting.lede}
            </p>
            {lastServiceTakenName ? (
              <p className="salon-service-start__last-service">
                Last service: <span>{lastServiceTakenName}</span>
              </p>
            ) : null}
          </div>
          <div className="salon-service-start__hero-art-wrap">
            <img
              className={
                catalogGender === 'male'
                  ? 'salon-service-start__hero-art salon-service-start__hero-art--male'
                  : 'salon-service-start__hero-art salon-service-start__hero-art--female'
              }
              src={heroImageUrl}
              alt=""
              aria-hidden="true"
              decoding="async"
            />
          </div>
        </section>
        </div>

        <div className="salon-service-start__left-stack">
          <aside
            className="salon-service-start__category-rail-host"
            ref={setCategoryRailHost}
            aria-label="Service category menu"
          />
        </div>

        {loading || servicesLoading ? (
          <p className="salon-service-session__lede salon-service-start__workspace-loading">Loading…</p>
        ) : (
          <>
            <section className="salon-service-session__panel salon-guest-pick__panel salon-service-start__staff-panel">
              <h2 className="salon-service-session__subtitle">Who is serving?</h2>
              {staffTierHint ? (
                <p className="salon-service-session__lede salon-service-start__staff-hint">{staffTierHint}</p>
              ) : null}
              {!staffCatalog.length ? (
                <p className="salon-guest-pick__empty">No stylists available.</p>
              ) : !visibleStaff.length ? (
                <p className="salon-guest-pick__empty">
                  No staff match the selected services. Change services or update staff roles in Partner
                  Staff.
                </p>
              ) : (
                <div
                  className="salon-service-session__staff-grid salon-guest-pick__staff"
                  role="group"
                  aria-label="Stylist"
                >
                  {visibleStaff.map((person) => {
                    const active = staffSelectedKeys.has(String(person.id));
                    const unavailable = staffDeskUnavailable(person, occupiedStylistIds);
                    return (
                      <button
                        key={person.id}
                        type="button"
                        className={
                          active
                            ? 'salon-service-session__staff is-active'
                            : `salon-service-session__staff${unavailable ? ' is-occupied' : ''}`
                        }
                        onClick={() => selectStaff(person)}
                        disabled={busy || unavailable}
                        aria-disabled={unavailable || undefined}
                        aria-pressed={active}
                      >
                        <span className="salon-service-session__staff-avatar" aria-hidden="true">
                          <img
                            src="/no-image.png"
                            alt=""
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <span className="salon-service-session__staff-initials">
                            {staffInitials(person.name)}
                          </span>
                        </span>
                        <span className="salon-service-session__staff-text">
                          <strong>{person.name}</strong>
                          <span>
                            {staffDeskPickSubtitle(person, occupiedStylistIds)}
                          </span>
                        </span>
                        <span
                          className={
                            active
                              ? 'salon-service-session__staff-check is-checked'
                              : 'salon-service-session__staff-check'
                          }
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="salon-service-session__panel salon-guest-pick__panel salon-service-start__services">
              {lastTakenServices.length || engagementBookingId ? (
                <div className="salon-guest-pick__panel-head">
                  <h2 className="salon-service-session__subtitle">Services</h2>
                  <div className="salon-guest-pick__panel-head-actions">
                    {engagementBookingId ? (
                      <button
                        type="button"
                        className="salon-service-session__link"
                        onClick={() => setAddServiceOpen(true)}
                        disabled={busy}
                      >
                        Add service
                      </button>
                    ) : null}
                    {lastTakenServices.length ? (
                      <button
                        type="button"
                        className="salon-service-session__link"
                        onClick={applyLastTaken}
                        disabled={busy}
                      >
                        Last visit
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {servicesCatalogError ? (
                <p className="salon-service-session__error">{servicesCatalogError}</p>
              ) : (
                <>
                  {!catalogGender && !servicesLoading ? (
                    <p className="salon-guest-pick__empty salon-service-start__gender-hint">
                      Guest gender not set yet — showing your franchise service list. Add gender at
                      check-in to filter the menu.
                    </p>
                  ) : null}
                <RegisterServicePicker
                  services={serviceOptions}
                  selected={selectedServices}
                  onToggle={toggleService}
                  onQuantityChange={changeServiceQuantity}
                  enableQuantity
                  disabled={busy}
                  categoryTabs
                  categoryTabsLayout="sidebar"
                  categoryRailHost={categoryRailHost}
                  cardLayout="stack"
                  variant="cards"
                  visualBundle="service-session"
                  visualGender={catalogGender}
                  showSearch
                  hint=""
                  title="Select Services"
                />
                </>
              )}
            </section>

          </>
        )}
        </div>

        {!loading && !servicesLoading ? (
          <footer className="salon-service-session__footer salon-guest-pick__footer salon-service-start__checkout-footer">
            <div className="salon-service-start__action-bar">
              {error ? <p className="salon-service-session__error">{error}</p> : null}
              {notice ? <p className="salon-service-session__notice">{notice}</p> : null}
              {selectedServices.length && selectedServicesTotal != null ? (
                <button
                  type="button"
                  className="salon-service-start__total salon-service-start__total--cta"
                  aria-live="polite"
                  aria-haspopup="dialog"
                  onClick={() => setCartModalOpen(true)}
                >
                  <span className="salon-service-start__total-label">Selected total</span>
                  <span className="salon-service-start__total-cta-wrap">
                    <strong className="salon-service-start__total-amount">
                      {formatDisplayPrice(selectedServicesTotal)}
                    </strong>
                    <span className="salon-service-start__total-hint" aria-hidden="true">View details</span>
                  </span>
                </button>
              ) : (
                <p className="salon-service-start__total salon-service-start__total--empty">
                  Select services to see total
                </p>
              )}
              <button
                type="button"
                className="salon-service-session__primary salon-service-session__primary--wide salon-service-start__start-btn"
                disabled={busy || !selectedStaff.length || !selectedServices.length}
                onClick={startService}
              >
                {busy
                  ? addMoreMode
                    ? 'Adding…'
                    : 'Starting…'
                  : addMoreMode
                    ? 'Add & start'
                    : `Start (${selectedServiceUnits} Service${selectedServiceUnits === 1 ? '' : 's'}) →`}
              </button>
            </div>
          </footer>
        ) : null}
        </div>
      </div>
      <ServiceSessionCartModal
        open={cartModalOpen}
        summary={selectedServicesSummary}
        onClose={() => setCartModalOpen(false)}
      />
      {addServiceOpen && engagementBookingId ? (
        <AddServiceModal
          booking={{ id: engagementBookingId, franchise_id: addServiceFranchiseId }}
          purpose="add_service"
          onClose={() => setAddServiceOpen(false)}
          onDone={() => {
            setAddServiceOpen(false);
            toast.success('Service added');
          }}
        />
      ) : null}
      <Toaster position="bottom-right" />
    </div>
  );
}
